import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import GlassPanel from '../../../components/base/GlassPanel';
import BlueprintGrid from '../../../components/feature/BlueprintGrid';
import OrbitalRing from '../../../components/feature/OrbitalRing';

const focusAreas = [
  {
    icon: 'ri-brain-line',
    title: 'Artificial Intelligence & Automation',
    description: 'Investing in cutting-edge AI technologies that transform industries and create sustainable competitive advantages through intelligent automation and machine learning solutions.'
  },
  {
    icon: 'ri-leaf-line',
    title: 'Clean Tech',
    description: 'Supporting innovative clean technology companies developing sustainable energy solutions, environmental technologies, and green infrastructure for a carbon-neutral future.'
  },
  {
    icon: 'ri-global-line',
    title: 'IoT & Advanced Technologies',
    description: 'Partnering with companies at the forefront of Internet of Things, advanced connectivity, and emerging technologies that enable the next generation of digital transformation.'
  }
];

export default function FocusSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section id="focus" ref={sectionRef} className="relative min-h-screen py-32 bg-gradient-to-br from-blue-900 via-slate-800 to-blue-800 overflow-hidden">
      {/* Background Elements */}
      <motion.div style={{ y: backgroundY }} className="absolute inset-0">
        <BlueprintGrid opacity={8} color="#ffffff" animated={false} />
      </motion.div>

      {/* Floating Parallax Elements */}
      <motion.div
        style={{ y: useTransform(scrollYProgress, [0, 1], ["0%", "40%"]) }}
        className="absolute -left-16 top-1/4"
      >
        <OrbitalRing 
          size={300} 
          color="#ffffff" 
          opacity={15}
          animate={true}
        />
      </motion.div>

      <motion.div
        style={{ y: useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]) }}
        className="absolute -right-16 bottom-1/4"
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="w-48 h-48 rounded-full border-2 border-white/15 relative"
        >
          <div className="absolute inset-4 rounded-full border border-white/10"></div>
          <div className="absolute inset-8 rounded-full border border-white/5"></div>
        </motion.div>
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.h2 
            className="text-5xl font-light text-white mb-6 tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Our Focus
          </motion.h2>
          <motion.div 
            className="w-24 h-1 bg-white mx-auto"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          />
        </div>

        {/* Focus Cards Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {focusAreas.map((area, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
            >
              <GlassPanel 
                className="p-12 h-full"
                blur={20}
                opacity={8}
                borderOpacity={30}
                glowColor="#169fdb"
              >
                {/* Icon */}
                <div className="mb-8">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mx-auto">
                    <i className={`${area.icon} text-2xl text-white`}></i>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-2xl font-semibold text-white mb-6 text-center">
                  {area.title}
                </h3>

                {/* Divider */}
                <div className="w-10 h-px bg-white/20 mx-auto mb-6"></div>

                {/* Description */}
                <p className="text-white/80 leading-relaxed text-center text-xl">
                  {area.description}
                </p>
              </GlassPanel>
            </motion.div>
          ))}
        </div>

        {/* Investment Philosophy */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed">
            Our investment philosophy is rooted in <strong className="text-blue-300">innovation</strong>, <strong className="text-blue-300">resilience</strong>, and <strong className="text-blue-300">enduring value creation</strong>.
          </p>
        </motion.div>
      </div>
    </section>
  );
}